<template>
	<div class="content">
		<div class="list-section">
			<div class="section-title">已设置的提醒（{{ reminderList.length }}）</div>
			<div v-if="reminderList.length === 0" class="empty-tip">暂无提醒，点击下方添加</div>
			<div
				v-for="item in reminderList"
				:key="item.id"
				class="reminder-item"
				:class="{ disabled: !item.enabled || item.doneToday }"
			>
				<div class="reminder-item-main" @click="editReminder(item)">
					<div class="reminder-item-text">{{ item.text || '（无内容）' }}</div>
					<div class="reminder-item-meta">
						<span>{{ item.startTime }} - {{ item.endTime }}</span>
						<span>每 {{ item.intervalMinutes }} 分钟</span>
						<span v-if="item.doneToday" class="tag-done">今日已完成</span>
						<span v-else-if="!item.enabled" class="tag-off">已停用</span>
						<span v-else class="tag-on">生效中</span>
					</div>
				</div>
				<van-button
					class="done-today-btn"
					size="small"
					:type="item.doneToday ? 'default' : 'primary'"
					plain
					:disabled="item.doneToday"
					:loading="completingId === item.id"
					@click.stop="markDoneToday(item)"
				>
					今日已完成
				</van-button>
			</div>
		</div>

		<van-button class="add-btn" type="primary" block @click="openAddDialog">添加提醒</van-button>

		<van-dialog
			v-model="showForm"
			:title="form.id ? '编辑提醒' : '添加提醒'"
			show-cancel-button
			:before-close="beforeDialogClose"
		>
			<div class="dialog-form">
				<van-field
					v-model="form.code"
					label="喵提醒码"
					placeholder="喵提醒码"
					clearable
				/>
				<div class="field-tip">在喵提醒创建提醒单后，复制喵码</div>
				<van-field
					v-model="form.text"
					type="textarea"
					rows="2"
					autosize
					label="提醒内容"
					placeholder="提醒内容"
					clearable
				/>
				<van-field
					readonly
					clickable
					label="生效开始"
					:value="form.startTime"
					placeholder="开始时间"
					@click="openTimePicker('startTime')"
				/>
				<van-field
					readonly
					clickable
					label="生效结束"
					:value="form.endTime"
					placeholder="结束时间"
					@click="openTimePicker('endTime')"
				/>
				<van-field
					v-model="form.intervalMinutes"
					type="digit"
					label="提醒间隔"
					placeholder="分钟"
				>
					<template #button>分钟</template>
				</van-field>
				<van-cell title="启用提醒">
					<template #right-icon>
						<van-switch v-model="form.enabled" size="20" />
					</template>
				</van-cell>
				<van-button
					v-if="form.id"
					class="delete-btn"
					type="danger"
					block
					plain
					:loading="deleting"
					@click="deleteReminder"
				>
					删除此提醒
				</van-button>
			</div>
		</van-dialog>

		<van-popup v-model="showTimePicker" position="bottom">
			<van-datetime-picker
				v-model="pickerTime"
				type="time"
				title="选择时间"
				@confirm="confirmTime"
				@cancel="showTimePicker = false"
			/>
		</van-popup>
	</div>
</template>

<script>
import {
	getMiaotixingList,
	saveMiaotixingConfig,
	deleteMiaotixingConfig,
	completeMiaotixingToday,
} from '../util/api'
import { Notify, Dialog } from 'vant'

const defaultForm = () => ({
	id: null,
	code: '',
	text: '',
	startTime: '09:00',
	endTime: '22:00',
	intervalMinutes: '30',
	enabled: true,
})

export default {
	name: 'MiaotixingCom',
	data() {
		return {
			reminderList: [],
			form: defaultForm(),
			showForm: false,
			showTimePicker: false,
			pickerTime: '09:00',
			activeTimeField: 'startTime',
			saving: false,
			deleting: false,
			completingId: null,
		}
	},
	mounted() {
		this.loadList()
	},
	methods: {
		normalizeTimeString(timeStr) {
			const [hour = '0', minute = '0'] = String(timeStr || '09:00').split(':')
			const h = Math.min(23, Math.max(0, Number(hour) || 0))
			const m = Math.min(59, Math.max(0, Number(minute) || 0))
			return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
		},
		formatPickerValue(value) {
			if (typeof value === 'string' && value.includes(':')) {
				return this.normalizeTimeString(value)
			}
			if (value instanceof Date && !isNaN(value.getTime())) {
				return this.normalizeTimeString(
					`${value.getHours()}:${value.getMinutes()}`
				)
			}
			return this.normalizeTimeString(this.pickerTime)
		},
		openTimePicker(field) {
			this.activeTimeField = field
			this.pickerTime = this.normalizeTimeString(this.form[field])
			this.showTimePicker = true
		},
		confirmTime(value) {
			this.form[this.activeTimeField] = this.formatPickerValue(value)
			this.showTimePicker = false
		},
		resetForm() {
			this.form = defaultForm()
		},
		openAddDialog() {
			this.resetForm()
			this.showForm = true
		},
		editReminder(item) {
			this.form = {
				id: item.id,
				code: item.code || '',
				text: item.text || '',
				startTime: item.startTime || '09:00',
				endTime: item.endTime || '22:00',
				intervalMinutes: String(item.intervalMinutes ?? 30),
				enabled: item.enabled !== 0,
			}
			this.showForm = true
		},
		loadList() {
			getMiaotixingList()
				.then((res) => {
					this.reminderList = res?.data?.data || []
				})
				.catch(() => {})
		},
		validateForm() {
			if (!this.form.code?.trim()) {
				Notify({ type: 'warning', message: '请填写喵提醒码' })
				return false
			}
			if (!this.form.text?.trim()) {
				Notify({ type: 'warning', message: '请填写提醒内容' })
				return false
			}
			const interval = Number(this.form.intervalMinutes)
			if (!interval || interval < 1) {
				Notify({ type: 'warning', message: '提醒间隔至少为 1 分钟' })
				return false
			}
			const [startH, startM] = this.form.startTime.split(':').map(Number)
			const [endH, endM] = this.form.endTime.split(':').map(Number)
			if (startH * 60 + startM >= endH * 60 + endM) {
				Notify({ type: 'warning', message: '结束时间需晚于开始时间' })
				return false
			}
			return true
		},
		buildPayload() {
			return {
				id: this.form.id || undefined,
				code: this.form.code.trim(),
				text: this.form.text.trim(),
				startTime: this.form.startTime,
				endTime: this.form.endTime,
				intervalMinutes: Number(this.form.intervalMinutes),
				enabled: this.form.enabled ? 1 : 0,
			}
		},
		beforeDialogClose(action, done) {
			if (action === 'cancel') {
				done()
				return
			}
			if (!this.validateForm()) {
				done(false)
				return
			}
			this.saving = true
			saveMiaotixingConfig(this.buildPayload())
				.then((res) => {
					if (res?.data?.error) {
						Notify({ type: 'danger', message: res.data.message || '保存失败' })
						done(false)
						return
					}
					Notify({ type: 'success', message: res?.data?.message || '保存成功' })
					this.loadList()
					done()
				})
				.catch(() => {
					Notify({ type: 'danger', message: '保存失败' })
					done(false)
				})
				.finally(() => {
					this.saving = false
				})
		},
		markDoneToday(item) {
			if (item.doneToday) return
			Dialog.confirm({
				title: '今日已完成',
				message: '确认后今天内不再发送该提醒，明天会自动恢复。',
			}).then(() => {
				this.completingId = item.id
				completeMiaotixingToday({ id: item.id })
					.then((res) => {
						if (res?.data?.code === 0) {
							Notify({ type: 'success', message: res.data.message || '今日已不再提醒' })
							this.loadList()
						} else {
							Notify({ type: 'danger', message: res?.data?.message || '操作失败' })
						}
					})
					.finally(() => {
						this.completingId = null
					})
			}).catch(() => {})
		},
		deleteReminder() {
			if (!this.form.id) return
			Dialog.confirm({ title: '确认删除该提醒？' }).then(() => {
				this.deleting = true
				deleteMiaotixingConfig({ id: this.form.id })
					.then((res) => {
						if (res?.data?.code === 0) {
							Notify({ type: 'success', message: '已删除' })
							this.showForm = false
							this.loadList()
						} else {
							Notify({ type: 'danger', message: res?.data?.message || '删除失败' })
						}
					})
					.finally(() => {
						this.deleting = false
					})
			}).catch(() => {})
		},
	},
}
</script>

<style scoped>
.content {
	/* Q版：柔和底色 + 贴纸卡片 */
	--q-bg0: #fff7fb;
	--q-bg1: #f2fbff;
	--q-card: rgba(255, 255, 255, 0.92);
	--q-line: rgba(15, 23, 42, 0.08);
	--q-shadow: 0 10px 24px rgba(17, 24, 39, 0.08);
	--q-shadow-sm: 0 6px 14px rgba(17, 24, 39, 0.08);
	--q-radius: 18px;
	--q-primary: #34c3ff;
	--q-primary-2: #6ee7ff;
	--q-danger: #ff5f7a;

	padding: 14px 14px 18px;
	height: 100%;
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	position: relative;
	font-family: "Comic Sans MS", "Microsoft YaHei UI", "PingFang SC", "Hiragino Sans GB", system-ui, -apple-system,
		BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
	background: radial-gradient(120% 90% at 20% 0%, var(--q-bg0) 0%, transparent 60%),
		radial-gradient(120% 90% at 90% 30%, var(--q-bg1) 0%, transparent 55%),
		#ffffff;
}

.content::before,
.content::after {
	content: "";
	position: fixed;
	z-index: -1;
	pointer-events: none;
	opacity: 0.9;
}
.content::before {
	width: 240px;
	height: 240px;
	left: -90px;
	top: 140px;
	background: radial-gradient(circle at 30% 30%, rgba(255, 184, 220, 0.55), transparent 60%),
		radial-gradient(circle at 70% 70%, rgba(110, 231, 255, 0.45), transparent 62%);
	border-radius: 999px;
}
.content::after {
	width: 280px;
	height: 280px;
	right: -140px;
	bottom: 90px;
	background: radial-gradient(circle at 40% 30%, rgba(196, 255, 214, 0.5), transparent 60%),
		radial-gradient(circle at 70% 70%, rgba(255, 224, 170, 0.45), transparent 62%);
	border-radius: 999px;
}
/* 不要对 `.content > *` 统一加 position/z-index，会破坏 Vant Dialog/Popup 的 fixed 布局 */

.list-section {
	flex: 1;
	overflow-y: auto;
	margin-bottom: 12px;
}

.section-title {
	font-size: 14px;
	font-weight: bold;
	margin-bottom: 12px;
	color: #333;
}

.empty-tip {
	color: #999;
	font-size: 13px;
	padding: 24px 0;
	text-align: center;
}

.reminder-item {
	background: var(--q-card);
	border-radius: var(--q-radius);
	border: 1px solid var(--q-line);
	box-shadow: var(--q-shadow-sm);
	padding: 12px;
	margin-bottom: 10px;
	display: flex;
	align-items: center;
	gap: 8px;
}

.reminder-item-main {
	flex: 1;
	min-width: 0;
}

.done-today-btn {
	flex-shrink: 0;
}

.reminder-item.disabled {
	opacity: 0.65;
}

.reminder-item-text {
	font-size: 15px;
	font-weight: 500;
	margin-bottom: 6px;
	word-break: break-all;
}

.reminder-item-meta {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	font-size: 12px;
	color: #888;
}

.tag-on {
	color: #07c160;
}

.tag-off {
	color: #ee0a24;
}

.tag-done {
	color: #969799;
}

.add-btn {
	flex-shrink: 0;
	border: none;
	border-radius: 999px;
	background: linear-gradient(180deg, var(--q-primary) 0%, var(--q-primary-2) 100%);
	box-shadow: 0 12px 24px rgba(52, 195, 255, 0.24);
}

.dialog-form {
	padding: 8px 0;
	max-height: 60vh;
	overflow-y: auto;
}

.field-tip {
	font-size: 12px;
	color: #888;
	padding: 0 16px 8px;
	line-height: 1.5;
}

.delete-btn {
	margin: 12px 16px 0;
	width: calc(100% - 32px);
}

.content /deep/ .van-dialog {
	border-radius: 18px;
	overflow: hidden;
}
.content /deep/ .van-dialog__confirm {
	border: none;
	background: linear-gradient(180deg, var(--q-primary) 0%, var(--q-primary-2) 100%);
	color: #fff;
	font-weight: 800;
}
.content /deep/ .van-dialog__cancel {
	color: rgba(47, 52, 64, 0.9);
	font-weight: 700;
}
.content /deep/ .van-button--danger.van-button--plain {
	border-color: rgba(255, 95, 122, 0.35);
	color: rgba(255, 95, 122, 0.95);
	border-radius: 999px;
}
.content /deep/ .van-button {
	border-radius: 999px;
}
</style>
